import './FeaturedProductsSection.css';

import { motion } from 'framer-motion';
import { useState } from 'react';
import { FaAmazon } from 'react-icons/fa';

import { getProductBySlug } from '../../data/products';
import FeaturedProduct from '../FeaturedProduct';
import WorkbookDownloadPopup from '../WorkbookDownloadPopup';

const FeaturedProductsSection = () => {
  const [isDownloadPopupOpen, setIsDownloadPopupOpen] = useState(false);
  const [selectedProductSlug, setSelectedProductSlug] = useState<string | null>(
    null,
  );

  // Featured products data
  const featuredProducts = [
    {
      id: 1,
      title: 'konnect better: unlocking the power of your relationships',
      subtitle:
        'Discover practical insights and real tools to understand yourself and strengthen your connections.',
      description:
        "Secure the first book in the konnect better three-part series. This collection is designed to be a transformative resource for relationship development. Each volume explores a core pillar of connection, and together they form a practical roadmap to help you build trust, improve communication, and navigate conflict with confidence.\n\nThis first volume is your starting point. Packed with research-backed insights, proven strategies, and hands-on exercises, it's a guide for anyone ready to deepen their relationships. Whether it's strengthening your bonds with loved ones, colleagues, or even yourself, this book offers a clear, actionable path to cultivating connections that truly last.",
      imageUrl: '/images/konnect_better_book(2).png',
      category: 'Books',
      features: [
        'Practical insights from modern psychology',
        'Hands-on exercises for relationship building',
        'Tools for self-awareness and emotional fluency',
      ],
      ctaText: 'Order Now',
      ctaLink: 'https://a.co/d/drPmmD8',
      storeLinks: [
        { name: 'Amazon', url: 'https://a.co/d/drPmmD8', icon: FaAmazon },
        {
          name: 'Barnes & Noble',
          url: 'https://www.barnesandnoble.com/w/konnect-better-mo-lidsky-phd/1148503636',
          iconText: 'B&N',
        },
        {
          name: 'Indigo',
          url: 'https://www.indigo.ca/products/konnect-better-unlocking-the-power-of-your-relationships-volume-one-committed-connections?variant=46646021193938',
          iconText: 'indigo',
        },
        {
          name: 'Walmart',
          url: 'https://www.walmart.com/ip/Konnect-Better-Unlocking-the-Power-of-Your-Relationships-Volume-One-Committed-Connections-Hardcover-9798891385795/18134763639',
          iconText: 'W',
        },
      ],
      award: {
        image: '/pinnacle-award.png',
        title: 'Pinnacle Book Achievement Award',
        description: 'Winner of the Pinnacle Book Achievement Award',
      },
    },
    {
      id: 2,
      title: 'the connection blueprint',
      subtitle:
        'Interactive exercises to help you explore your values, strengths, and growth areas.',
      description:
        'Step into clarity. This playbook is designed to spark powerful self-discovery and stronger relationships with a little practice. We believe that before we can deepen our connections with others, we need to first understand ourselves. Through self-guided exercises and research-backed insights, you can illuminate your core values and natural strengths. By helping you recognize your patterns, it sets the stage for a more intentional way of relating to yourself and others.',
      imageUrl: '/images/the_connection_blueprint.png',
      category: 'Playbooks',
      features: [
        'Self-guided exercises for self-discovery',
        'Research-backed insights',
        'Explore your core values and strengths',
        'Build more intentional relationships',
      ],
      ctaText: 'Download Free Workbook',
      ctaOnClick: () => {
        setSelectedProductSlug('the-connection-blueprint');
        setIsDownloadPopupOpen(true);
      },
    },
    {
      id: 3,
      title: 'the konnection game',
      subtitle:
        'Interactive prompts and reflections designed to help families transform their relationships.',
      description:
        'A physical and digital card game designed for groups. This edition can help families transform their relationships through interactive prompts and thoughtful insights that can turn meaningful connections into a shared experience. This game can spark honest conversations and reflections that can help friends, families, or teams explore new sides of each other. Perfect for dinner tables, retreats, or gatherings, it creates an atmosphere where everyone feels seen, valued, and closer one card at a time.',
      imageUrl: '/images/konnect_better_card.png',
      category: 'Card Games',
      features: [
        'Prompts for honest conversations',
        'Perfect for groups, friends, and families',
        'Creates meaningful shared experiences',
        'Fosters an atmosphere of connection',
      ],
      ctaText: 'Join the Community',
      ctaLink: '/joincommunity',
    },
  ];

  return (
    <section
      id="featured-products"
      className="py-8 md:py-12 bg-surface dark:bg-panel/95"
    >
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-8"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-primary dark:text-theme-primary mb-4">
            tools for you
          </h2>
          <div className="w-24 h-1 bg-secondary mx-auto mb-6"></div>
          <p className="text-lg text-theme-secondary max-w-3xl mx-auto">
            Discover tools designed to help families transform their
            relationships.
          </p>
        </motion.div>

        <div className="featured-products-container">
          {featuredProducts.map((product, index) => (
            <div key={product.id} className="featured-product-item">
              <FeaturedProduct
                title={product.title}
                subtitle={product.subtitle}
                description={product.description}
                imageUrl={product.imageUrl}
                category={product.category}
                features={product.features}
                ctaText={product.ctaText}
                ctaLink={product.ctaLink}
                ctaOnClick={product.ctaOnClick}
                storeLinks={product.storeLinks}
                reversed={index % 2 !== 0}
                award={product.award}
              />
            </div>
          ))}
        </div>
      </div>
      <WorkbookDownloadPopup
        isOpen={isDownloadPopupOpen}
        onClose={() => setIsDownloadPopupOpen(false)}
        product={
          selectedProductSlug
            ? getProductBySlug(selectedProductSlug) || null
            : null
        }
      />
    </section>
  );
};

export default FeaturedProductsSection;
